import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Stack, Tooltip, Typography, useTheme, ClickAwayListener } from '@mui/material';

import { Close } from '../../icons';
import { main300Color, main400Color } from '../tokenColorTemporary';

import type { TooltipProps } from '@mui/material';
import type { ReactElement, ReactNode } from 'react';

export interface EliceTooltipProps extends TooltipProps {
  title: string | ReactNode;
  isControl?: boolean;
  children: ReactElement<any, any>;
  initOpen?: boolean;
  triggerAction?: 'default' | 'click';
  backgroundColor?: string;
  closeColor?: string;
  fontColor?: string;
  closeOnScroll?: boolean;
}

const StyledScrollBar = styled(Typography)`
  ::-webkit-scrollbar {
    width: 0.5rem;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    display: none;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: ${main300Color};
    border-radius: 0.5rem;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: ${main400Color};
  }
`;

const EliceTooltip = ({
  title,
  initOpen = false,
  isControl,
  placement = 'left',
  triggerAction = 'default',
  children,
  backgroundColor,
  closeColor,
  fontColor,
  closeOnScroll = false,
  ...rest
}: EliceTooltipProps) => {
  const theme = useTheme();
  const slotProps = {
    tooltip: {
      style: {
        backgroundColor: backgroundColor || theme.palette.primary.main,
        color: fontColor || '#fff',
        borderRadius: '1rem',
        padding: '0.75rem',
        maxWidth: '15.7rem',
        maxHeight: '16rem',
      },
    },
    arrow: {
      style: {
        color: backgroundColor || theme.palette.primary.main,
        fontSize: '0.75rem',
      },
    },
  };

  const [open, setOpen] = useState(false);
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setOpen(initOpen);
      return;
    }
  }, [initOpen]);

  React.useEffect(() => {
    const handleScroll = () => {
      setOpen(false);
    };

    if (open && closeOnScroll) {
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [open, closeOnScroll]);

  return isControl ? (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip
        arrow
        title={
          <Stack direction="row" gap="0.625rem">
            <Close
              style={{
                opacity: 0.7,
                flexShrink: 0,
                cursor: 'pointer',
              }}
              color={closeColor || 'currentColor'}
              onClick={() => setOpen(false)}
            />
            <StyledScrollBar
              style={{
                maxHeight: '15rem',
                overflowY: 'auto',
              }}
            >
              <Typography variant="subtitle2">{title}</Typography>
            </StyledScrollBar>
          </Stack>
        }
        placement={placement}
        open={open}
        onOpen={() => {
          if (triggerAction === 'default') {
            setOpen(true);
          }
        }}
        onClick={() => {
          if (triggerAction === 'click') {
            setOpen(true);
          }
        }}
        slotProps={slotProps}
        style={{
          cursor: triggerAction === 'click' ? 'pointer' : 'auto',
        }}
        {...rest}
      >
        <Stack position="relative" width="fit-content" height="fit-content" whiteSpace="pre-wrap">
          {children}
        </Stack>
      </Tooltip>
    </ClickAwayListener>
  ) : (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Tooltip
        arrow
        title={
          <StyledScrollBar
            style={{
              fontWeight: 700,
              fontSize: '1.375rem',
              maxHeight: '15rem',
              overflowY: 'auto',
            }}
          >
            <Typography variant="subtitle2">{title}</Typography>
          </StyledScrollBar>
        }
        placement={placement}
        slotProps={slotProps}
        {...rest}
      >
        <Stack position="relative" width="fit-content" height="fit-content">
          {children}
        </Stack>
      </Tooltip>
    </ClickAwayListener>
  );
};
export default EliceTooltip;
